// API-Schicht.
//
// Anmeldung läuft über POST /api/session; danach trägt ein HttpOnly-Cookie
// die Sitzung. Das Token liegt damit weder in localStorage (wo jedes Skript
// drankommt) noch in URLs (wo es in Logs und Verläufen landet).
import type { UserView } from '@speis/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Netzwerkfehler, nicht Serverfehler – die Aktion darf in die Outbox. */
  get isOffline(): boolean {
    return this.status === 0;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T = unknown>(
  path: string,
  { method = 'GET', body, signal }: RequestOptions = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'Keine Verbindung zum Server.');
  }

  if (res.status === 204) return undefined as T;

  let payload: unknown = null;
  const isJson = res.headers.get('content-type')?.includes('application/json');
  if (isJson) {
    payload = await res.json().catch(() => null);
  }

  if (!res.ok) {
    const err = payload as { error?: string; details?: unknown } | null;
    throw new ApiError(res.status, err?.error || `Fehler ${res.status}`, err?.details);
  }

  return payload as T;
}

// ── Sitzung ───────────────────────────────────────────────────────────

export interface SessionInfo {
  id: string;
  userAgent: string | null;
  createdAt: string;
  lastSeen: string;
  current: boolean;
}

/** Prüft, ob eine gültige Sitzung besteht. */
export async function currentSession(): Promise<{
  me: UserView;
  sessions: SessionInfo[];
} | null> {
  try {
    return await api('/api/session');
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export async function login(token: string): Promise<UserView> {
  const { me } = await api<{ me: UserView }>('/api/session', {
    method: 'POST',
    body: { token: token.trim() },
  });
  return me;
}

export function logout(): Promise<void> {
  return api('/api/session', { method: 'DELETE' });
}

export function logoutEverywhere(): Promise<void> {
  return api('/api/session/all', { method: 'DELETE' });
}

/**
 * Erst-Anmeldung per Link (`/?token=…`). Das Token wird sofort gegen ein
 * Cookie getauscht und aus der Adresszeile entfernt, damit es nicht im
 * Verlauf stehen bleibt.
 */
export async function consumeTokenFromUrl(): Promise<boolean> {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('token');
  if (!token) return false;

  url.searchParams.delete('token');
  window.history.replaceState({}, '', url.pathname + url.search + url.hash);

  try {
    await login(token);
    return true;
  } catch {
    return false;
  }
}
